import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Loader2, Megaphone, CheckCircle2, Share2, MapPin, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { full_name: "", email: "", location: "", comment: "", display_publicly: true };

export default function PetitionPublic() {
  const { slug } = useParams();
  const [petition, setPetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/petitions/${slug}`);
      setPetition(data);
    } catch (_e) {
      setPetition(null);
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const sign = async (e) => {
    e.preventDefault();
    setSigning(true);
    try {
      const { data } = await api.post(`/petitions/${slug}/sign`, form);
      setSigned(true);
      if (data.already_signed) toast.info("You've already signed this petition.");
      else toast.success("Thank you for signing!");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSigning(false); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: petition.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch (err) {
      // navigator.share rejects when the user dismisses the share sheet — that's not an error.
      if (err && err.name !== "AbortError") {
        console.warn("Share failed:", err);
      }
    }
  };

  if (loading) {
    return <div className="bg-black min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-400"/></div>;
  }
  if (!petition) {
    return (
      <div className="bg-black text-zinc-100 min-h-screen pt-20 pb-24" data-testid="petition-not-found">
        <div className="container-pl max-w-xl text-center">
          <Megaphone className="h-10 w-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.5}/>
          <h1 className="display-serif text-3xl text-zinc-50 mb-3">Petition not found.</h1>
          <p className="text-zinc-400 mb-6">This petition may have been removed or never published.</p>
          <Link to="/community"><Button variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30"><ArrowLeft className="mr-2 h-4 w-4"/> Back to Community</Button></Link>
        </div>
      </div>
    );
  }

  const goal = Math.max(100, Math.ceil((petition.signature_count + 1) / 100) * 100);
  const pct = Math.min(100, (petition.signature_count / goal) * 100);

  return (
    <div className="bg-black text-zinc-100 min-h-screen" data-testid="petition-public-page">
      {/* Hero */}
      <section className="pt-12 pb-10 border-b border-zinc-900">
        <div className="container-pl max-w-3xl">
          <Link to="/community" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-amber-400 mb-6 transition-colors"><ArrowLeft className="h-3.5 w-3.5"/> Community</Link>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-400 border border-amber-400/40 rounded-sm px-2 py-0.5">{petition.category}</span>
            {petition.target_audience && <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">→ {petition.target_audience}</span>}
          </div>
          <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-[1.05] mb-5 accent-rule">
            {petition.title}
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed mb-8 whitespace-pre-wrap">{petition.cause}</p>

          <div className="grid sm:grid-cols-[1fr_auto] items-end gap-6">
            <div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="display-serif text-4xl text-amber-400" data-testid="signature-count">{petition.signature_count.toLocaleString()}</span>
                <span className="text-sm text-zinc-400">of {goal.toLocaleString()} signatures</span>
              </div>
              <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-blue-500" style={{ width: `${pct}%` }}/>
              </div>
            </div>
            <Button onClick={share} variant="outline" className="rounded-sm border-zinc-700 text-zinc-300 hover:border-amber-400 hover:text-amber-400" data-testid="petition-share-btn">
              <Share2 className="mr-2 h-4 w-4" strokeWidth={1.5}/> Share
            </Button>
          </div>
        </div>
      </section>

      {/* Why it matters / action */}
      {(petition.impact || petition.proposed_action) && (
        <section className="py-12 border-b border-zinc-900 bg-zinc-950/40">
          <div className="container-pl max-w-3xl grid md:grid-cols-2 gap-8">
            {petition.impact && (
              <div>
                <p className="overline mb-3">Why this matters</p>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{petition.impact}</p>
              </div>
            )}
            {petition.proposed_action && (
              <div>
                <p className="overline mb-3">What we're asking for</p>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{petition.proposed_action}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sign form + signature wall */}
      <section className="py-14">
        <div className="container-pl max-w-3xl grid lg:grid-cols-2 gap-10">
          <div>
            {signed ? (
              <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-8 text-center" data-testid="petition-signed">
                <CheckCircle2 className="h-10 w-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5}/>
                <h2 className="display-serif text-3xl text-zinc-50 mb-2">Signed.</h2>
                <p className="text-sm text-zinc-300 mb-6">Your voice has been added. Share this petition to grow the impact.</p>
                <Button onClick={share} variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30">
                  <Share2 className="mr-2 h-4 w-4" strokeWidth={1.5}/> Share with others
                </Button>
              </div>
            ) : (
              <>
                <p className="overline mb-3">Add your name</p>
                <h2 className="display-serif text-2xl sm:text-3xl text-zinc-50 mb-5 leading-tight">Sign this petition.</h2>
                <form onSubmit={sign} className="space-y-4" data-testid="petition-sign-form">
                  <div>
                    <Label className="text-zinc-300">Full name <span className="text-amber-400">*</span></Label>
                    <Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="sign-name"/>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Email <span className="text-amber-400">*</span></Label>
                    <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="sign-email"/>
                    <p className="text-[11px] text-zinc-500 mt-1">Never shown publicly.</p>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Location (optional)</Label>
                    <Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="e.g., Brooklyn, NY" className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="sign-location"/>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Why you're signing (optional)</Label>
                    <Textarea rows={3} value={form.comment} onChange={(e) => update("comment", e.target.value)} placeholder="A line or two that may inspire others." className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="sign-comment"/>
                  </div>
                  <label className="flex items-start gap-3 border border-zinc-800 rounded-sm px-4 py-3 bg-zinc-950/60 cursor-pointer">
                    <input type="checkbox" checked={form.display_publicly} onChange={(e) => update("display_publicly", e.target.checked)} className="mt-1 accent-amber-400" data-testid="sign-display"/>
                    <span className="text-xs text-zinc-300">Display my name and comment on the public signature wall.</span>
                  </label>
                  <Button type="submit" size="lg" disabled={signing} className="w-full rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="sign-submit">
                    {signing ? <Loader2 className="h-4 w-4 animate-spin"/> : "Sign the petition"}
                  </Button>
                  <p className="text-[11px] text-zinc-500 italic">By signing, you agree to be contacted about this petition only. We never sell or share your information.</p>
                </form>
              </>
            )}
          </div>

          <div>
            <p className="overline mb-3">Voices for this cause</p>
            <h2 className="display-serif text-2xl sm:text-3xl text-zinc-50 mb-5 leading-tight">Who's signed.</h2>
            {petition.signatures.length === 0 ? (
              <div className="border border-zinc-800 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="signatures-empty">
                Be the first to sign. Your name will appear here.
              </div>
            ) : (
              <div className="space-y-3" data-testid="signatures-wall">
                {petition.signatures.map((s) => (
                  <div key={s.id} className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/60">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-medium text-zinc-100">{s.full_name}</p>
                      {s.location && <p className="text-[11px] text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3"/>{s.location}</p>}
                    </div>
                    {s.comment && <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">"{s.comment}"</p>}
                  </div>
                ))}
                {petition.signature_count > petition.signatures.length && (
                  <p className="text-xs text-zinc-500 italic text-center pt-2">
                    + {(petition.signature_count - petition.signatures.length).toLocaleString()} more signatures
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="pt-2 pb-14">
        <div className="container-pl max-w-3xl">
          <p className="text-[11px] text-zinc-500 italic">
            This petition was reviewed and published by Paralegal Voices of New York. We are not attorneys and do not provide legal advice. Petition outcomes are not guaranteed.
          </p>
        </div>
      </section>
    </div>
  );
}
